import { DateTime } from 'luxon';
import { LocalStorage } from 'quasar';
import { I18N } from 'constants/webStorage';

const getLocale = () => {
  const locale = LocalStorage.get.item(I18N) || 'pt-br';
  const bases = locale.split('-');

  return bases.length > 1 ? `${bases[0]}-${bases[1].toUpperCase()}` : locale;
};

const setLocal = (dateTime, local = false) => (local ? dateTime.toLocal() : dateTime);

function fixDST(dateTime, fromFormatType, fromFormat) {
  let baseDate;
  const locale = getLocale();
  const opts = { setZone: true };
  switch (fromFormatType) {
    case 'ISO':
      baseDate = DateTime.fromISO(dateTime, opts).setLocale(locale);
      break;
    case 'SQL':
      baseDate = DateTime.fromSQL(dateTime, opts).setLocale(locale);
      break;
    case 'FORMAT':
      baseDate = DateTime.fromFormat(dateTime, fromFormat, opts).setLocale(locale);
      break;
    default:
      baseDate = DateTime.fromJSDate(dateTime).setLocale(locale);
      break;
  }
  return baseDate;
}

const getDateTime = (dateTime, fromFormatType, fromFormat) => fixDST(dateTime,
  fromFormatType,
  fromFormat);

function convertDate(dateTime, toLocal, fromFormatType, fromFormat) {
  const convertedDate = getDateTime(dateTime, fromFormatType, fromFormat);

  return {
    date: setLocal(convertedDate, toLocal).toJSDate(),
    iso: setLocal(convertedDate, toLocal).toISO(),
    dateTime: setLocal(convertedDate, toLocal),
  };
}

function convertTimeZone(baseDate, timezone = 'America/Sao_Paulo', toLocal, fromFormatType, fromFormat) {
  const convertedDate = getDateTime(baseDate, fromFormatType, fromFormat)
  .setZone(timezone);

  return {
    date: setLocal(convertedDate, toLocal).toJSDate(),
    iso: setLocal(convertedDate, toLocal).toISO(),
    dateTime: setLocal(convertedDate, toLocal),
  };
}

function singleConverter(payload, options) {
  const {
    toIso,
    toJsDate,
    noTimeZone,
    toLocal,
    timeZone,
    format,
    fromFormatType,
    fromFormat,
  } = options;
  const converted = noTimeZone || !timeZone
    ? convertDate(payload, toLocal, fromFormatType, fromFormat)
    : convertTimeZone(payload, timeZone, toLocal, fromFormatType, fromFormat);

  if (format) return converted.dateTime.toFormat(format);

  if (toJsDate) return converted.date;

  if (toIso) return converted.iso;

  return converted.dateTime;
}

export function createDate(dateTime, options = {
  toIso: true,
  toJsDate: false,
  noTimeZone: false,
  toLocal: false,
  timeZone: '',
  format: '',
  fromFormatType: 'ISO',
  fromFormat: '',
}) {
  const baseOptions = {
    toIso: true,
    toJsDate: false,
    noTimeZone: false,
    toLocal: false,
    timeZone: '',
    format: '',
    fromFormatType: 'ISO',
    fromFormat: '',
  };

  const mergedOptions = {
    ...baseOptions,
    ...options,
  };

  return singleConverter(dateTime, mergedOptions);
}

export function getDate(dateTime) {
  return DateTime.fromSQL(dateTime);
}

export function getDaySuffix(day) {
  if (day >= 11 && day <= 13) {
    return 'th';
  }

  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

export function isoToLocale(dateTime) {
  const locale = getLocale();
  const offsetIndex = dateTime.lastIndexOf('-');
  const dtString = dateTime.substring(0, offsetIndex);

  const dt = DateTime.fromISO(dtString);
  const suffix = (locale === 'en-US') ? getDaySuffix(dt.day) : '';
  const formattedDate = dt.setLocale(locale).toLocaleString({ month: 'long', day: 'numeric' });

  return formattedDate.replace(/(\d+)/, `$1${suffix}`);
}

export default {
  createDate,
  getDate,
  isoToLocale,
};
